import {
  ERROR_MESSAGES,
  FormInput,
  MASKS,
  PATTERNS,
  PLACEHOLDERS,
} from "@farzoom/common-ui-components";
import { Checkbox, Col, Radio, Row, Space, Typography } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { disabledDate, useStyles } from "./personInfoModal";
import { notNullish } from "@utils/notNullish";
import { Nullish } from "types/helpers";
import { VerticalSpace } from "@app/UI/VerticalSpace";
import { FormInputDateTime } from "@app/common-ui-components/FormInputDateTime";
import { AddressSearch } from "@app/common-ui-components";
import { EmailInput } from "@app/UI/emailInput";
import { maskedSnils, SNILS_WARNING_MESSAGE, validateSnils } from "./utils/validateSnils";
import { INN12_WARNING_MESSAGE, validateInn12 } from "./utils/validateInn12";

const { Text } = Typography;

export const PersonInfoBlock = ({ control, setValue, personW, errors, getValues, readonly }) => {
  const classes = useStyles();

  const [actualAddress, registrationAddress] = getValues([
    "person.actualAddress",
    "person.registrationAddress",
  ]);
  const [actualAsRegistration, setActualAsRegistration] = useState(
    actualAddress === registrationAddress && notNullish(registrationAddress)
  );

  const onChangeAsRegistration = (e: CheckboxChangeEvent) => {
    setActualAsRegistration(e.target.checked);

    if (e.target.checked) {
      setValue("person.actualAddress", personW?.person?.registrationAddress);
    }
  };

  const onRegistrationAddressChange = (value: Nullish<string>) => {
    if (actualAsRegistration) {
      setValue("person.actualAddress", value);
    }
  };

  const onBlur = (key, value) => () => {
    setValue(key, value.trim());
  };

  return (
    <VerticalSpace className={classes.block}>
      <Row gutter={[8, 4]}>
        <Col span={8}>
          <FormInput
            name="person.lastName"
            control={control}
            label="Фамилия"
            required
            onBlur={onBlur("person.lastName", personW.person.lastName)}
          />
        </Col>
        <Col span={8}>
          <FormInput
            name="person.firstName"
            control={control}
            label="Имя"
            required
            onBlur={onBlur("person.firstName", personW.person.firstName)}
          />
        </Col>
        <Col span={8}>
          <FormInput
            name="person.secondName"
            control={control}
            label="Отчество"
            onBlur={onBlur("person.secondName", personW.person.secondName)}
          />
        </Col>
        <Col span={24}>
          <VerticalSpace size={2}>
            <Controller
              name="person.gender"
              control={control}
              rules={{ required: true }}
              render={() => (
                <Radio.Group
                  value={personW?.person?.gender}
                  onChange={(e) => setValue("person.gender", e.target.value)}
                >
                  <Radio value="male">М</Radio>
                  <Radio value="female">Ж</Radio>
                </Radio.Group>
              )}
            />
            {errors?.person?.gender && <Text type="danger">Укажите пол</Text>}
          </VerticalSpace>
        </Col>
      </Row>

      <Row gutter={8}>
        <Col span={12}>
          <Controller
            name="person.inn"
            control={control}
            rules={{ validate: (v) => !v || validateInn12(v) || INN12_WARNING_MESSAGE }}
            render={({ field, fieldState }) => {
              return <>
                <FormInput
                  {...field}
                  value={field.value}
                  onChange={(e: any) => {
                    field.onChange(!e.unmaskedValue ? null : e.unmaskedValue); // в форму пишем без маски
                  }}
                  label="ИНН"
                  mask="000000000000"
                  status={fieldState.error ? 'error' : undefined}
                />
                {fieldState.error && <Text type="danger">{INN12_WARNING_MESSAGE}</Text>}
              </>
            }}
          />
        </Col>
        <Col span={12}>
          <Controller
            name="person.snils"
            control={control}
            rules={{ validate: (v) => !v || validateSnils(v) || SNILS_WARNING_MESSAGE }}
            render={({ field, fieldState }) => {
              return <>
                <FormInput
                  {...field}
                  value={maskedSnils(field.value)} // Для отображения формируем по маске
                  onChange={(e: any) => {
                    field.onChange(!e.unmaskedValue ? null : e.unmaskedValue); // в форму пишем без маски
                  }}
                  label="СНИЛС"
                  mask="000-000-000 00"
                  status={fieldState.error ? 'error' : undefined}
                />
                {fieldState.error && <Text type="danger">{SNILS_WARNING_MESSAGE}</Text>}
              </>
            }}
          />
        </Col>
      </Row>
      <Space>
        <FormInputDateTime
          name="person.birthDate"
          control={control}
          setValue={setValue}
          label="Дата рождения"
          disabledDate={disabledDate}
          disabled={readonly}
        />
        <FormInput name="person.birthPlace" control={control} label="Место рождения" />
      </Space>
      <AddressSearch
        prefix="person.registrationAddress"
        control={control}
        setValue={setValue}
        label="Адрес регистрации"
        controlled
        parentOnChange={onRegistrationAddressChange}
      />
      <VerticalSpace size={4}>
        <div className={classes.label}>Адрес местонаходения:</div>
        <Checkbox checked={actualAsRegistration} onChange={onChangeAsRegistration}>
          Совпадает с адресом регистрации
        </Checkbox>
        {!actualAsRegistration && (
          <AddressSearch
            prefix="person.actualAddress"
            control={control}
            setValue={setValue}
            controlled
          />
        )}
      </VerticalSpace>
      <Row gutter={8}>
        <Col span={12}>
          <FormInput
            name="person.phone"
            control={control}
            label="Телефон"
            placeholder={PLACEHOLDERS.phone}
            mask={MASKS.phone}
            pattern={{
              value: PATTERNS.phone,
              message: ERROR_MESSAGES.phone,
            }}
          />
        </Col>
        <Col span={12}>
          <EmailInput name="person.email" control={control} setValue={setValue} />
        </Col>
      </Row>
    </VerticalSpace>
  );
};
